import { TableCell, TableRow } from "@/components/ui/table";
import { UserInfo } from "./user-row/UserInfo";
import { RoleSelect } from "./user-row/RoleSelect";
import { UserStatus } from "./user-row/UserStatus";
import { UserActions } from "./user-row/UserActions";
import { ResetPasswordButton } from "./user-row/ResetPasswordButton";
import { PaymentForm } from "./payments/PaymentForm";
import { supabase } from "@/integrations/supabase/client";
import { addDays, isAfter } from "date-fns";
import { UserPaymentStatus } from "./user-row/UserPaymentStatus";
import { PaymentStatus } from "@/hooks/admin/useUserPayments";
import { DebtInfo } from "@/hooks/admin/useMemberDebts";
import { EditUserDialog } from "./EditUserDialog";
import { Profile } from "@/hooks/profile/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Pencil } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { UserDetailDialog } from "./UserDetailDialog";
import { DebtActionButton } from "@/components/admin/debts/DebtActionButton";

interface UserRowProps {
  user: Profile;
  index: number;
  isMobile: boolean;
  onBanUser: (userId: string, banned: boolean) => void;
  onDeleteClick: (user: Profile) => void;
  onRoleChange: (userId: string, newRole: string) => void;
  currentUser?: Profile | null;
  paymentStatus?: PaymentStatus;
  /** True when the current user has the recepcionista role granted (via has_role),
   *  even if their primary profile.role is different (e.g. profesor with dual access). */
  actsAsRecepcionista?: boolean;
  isMastergym?: boolean;
  debtInfo?: DebtInfo;
}

export const UserRow = ({
  user,
  index,
  isMobile,
  onBanUser,
  onDeleteClick,
  onRoleChange,
  currentUser,
  paymentStatus,
  actsAsRecepcionista = false,
  isMastergym = false,
  debtInfo,
}: UserRowProps) => {
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const isAdmin = currentUser?.role === "admin";
  const isManager = currentUser?.role === "manager";
  const isRecepcionista = actsAsRecepcionista || currentUser?.role === "recepcionista";

  // Manager no puede editar su propio rol, el de monitores ni el de otros managers
  const canEditRole = isAdmin ||
  isManager &&
  user.id !== currentUser?.id &&
  user.role !== "monitor" &&
  user.role !== "manager" &&
  !!user.gym_id;

  const canPerformActions = isAdmin || isManager && !!user.gym_id;
  const canRegisterAndEdit = canPerformActions || (isRecepcionista && !!user.gym_id);

  const isPending = user.isPending === true;

  return (
    <>
    <TableRow
      className={`cursor-pointer ${
      index % 2 === 0 ?
      "bg-transparent" :
      "bg-muted/50"}`}
      onClick={() => setDetailOpen(true)}
      >

      <TableCell>
        <div className="flex flex-col gap-1">
          <UserInfo
            firstName={user.first_name}
            lastName={user.last_name}
            username={user.username}
            role={user.role}
            dni={user.dni}
            isMobile={isMobile}
            onRoleChange={(value) => onRoleChange(user.id, value)}
            canEditRole={canPerformActions} />

          {isPending &&
          <Badge variant="secondary" className="w-fit text-xs gap-1">
              <Clock className="h-3 w-3" />
              Pendiente de registro
            </Badge>
          }
        </div>
      </TableCell>
      
      {!isMobile &&
      <TableCell className="whitespace-nowrap">
          {canEditRole ?
        <RoleSelect
          role={user.role}
          onRoleChange={(value) => onRoleChange(user.id, value)}
          disabled={false}
          allowedRoles={['socio', 'profesor', 'recepcionista']} /> :

        null}
        </TableCell>
      }
      
      {isMobile ?
      <UserPaymentStatus userId={user.id} paymentStatus={paymentStatus} debtInfo={isMastergym ? debtInfo : undefined} /> :

      <TableCell className="whitespace-nowrap">
          <UserStatus banned={user.banned} />
        </TableCell>
      }

      {!isMobile &&
      <UserPaymentStatus userId={user.id} paymentStatus={paymentStatus} debtInfo={isMastergym ? debtInfo : undefined} />
      }
      
      <TableCell className="text-right" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-2">
          {canRegisterAndEdit &&
          <>
              <PaymentForm
              user={user}
              gymId={user.gym_id || null}
              onPaymentRegistered={() => {
                queryClient.invalidateQueries({ queryKey: ["lastPayment", user.id] });
              }} />

              {isMastergym && canRegisterAndEdit && user.gym_id && (
                <DebtActionButton
                  userId={user.id}
                  gymId={user.gym_id}
                  userName={`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.username || undefined}
                  debtInfo={debtInfo}
                />
              )}
              <Button
              variant="outline"
              size="icon"
              className="sm:px-[15px] sm:w-auto"
              onClick={() => setEditOpen(true)}
              onMouseEnter={() => {
                queryClient.prefetchQuery({
                  queryKey: ["active-payments", user.id],
                  queryFn: async () => {
                    const { data, error } = await supabase.
                    from("payments").
                    select("id, amount, description, paid_at, validity_days").
                    eq("user_id", user.id).
                    order("paid_at", { ascending: false });
                    if (error) throw error;
                    return (data || []).filter((p) => {
                      if (!p.paid_at) return false;
                      const expiration = addDays(new Date(p.paid_at), p.validity_days);
                      return isAfter(expiration, new Date());
                    });
                  }
                });
              }}>

                <Pencil className="h-4 w-4" />
              </Button>
              {canPerformActions && !isPending && user.role !== "admin" && user.role !== "manager" && (
                <ResetPasswordButton
                  userId={user.id}
                  userName={`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.username || "este usuario"}
                />
              )}
              {canPerformActions && (
                <UserActions
                onBan={() => onBanUser(user.id, !user.banned)}
                onDelete={() => onDeleteClick(user)}
                isRemoveFromGym={true} />
              )}
              <EditUserDialog user={user} open={editOpen} onOpenChange={setEditOpen} />
            </>
          }
        </div>
      </TableCell>
    </TableRow>

    <UserDetailDialog
      user={user}
      open={detailOpen}
      onOpenChange={setDetailOpen}
      onEditClick={() => setEditOpen(true)}
    />
    </>);

};